import { User } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { track } from './analytics';

// "Share Yhuu with 3 friends → see who opened your sessions for 3 days".
// The rules live in the database (supabase/referrals.sql); this file only
// remembers the invite code a visitor arrived with and calls those functions.
// Everything is wrapped so that, if the SQL hasn't been run yet, the app just
// behaves as before instead of breaking.

const REF_KEY = 'yhuu_ref';

export interface ReferralStatus {
  code: string | null;
  friends: number;        // friends who joined through my link AND sent a session
  pending?: number;       // friends who joined but haven't sent a session yet
  progress: number;       // 0–2: friends toward the next unlock
  credits: number;        // unlocks earned but not started yet
  window_until: string | null; // active 3-day window end, if any
}

export const inviteLink = (code: string) => `https://yhuu.life/?ref=${code}`;

export function captureRefFromUrl() {
  try {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (ref && /^[a-z0-9_-]{3,40}$/i.test(ref)) localStorage.setItem(REF_KEY, ref.toLowerCase());
  } catch { /* ignore */ }
}

function storedRef(): string | null {
  try { return localStorage.getItem(REF_KEY); } catch { return null; }
}

// Called for a signed-in user. If they came through someone's link, credit that
// person (once, never for yourself). Normally the database has already recorded
// them the moment they signed up — the code is stored with the account, so no
// email confirmation or login is needed. This call is the backup for sign-ups
// where the code couldn't be stored (e.g. Google sign-in).
export async function claimPendingReferral(user: User) {
  try {
    const doneKey = `yhuu_ref_done_${user.id}`;
    if (localStorage.getItem(doneKey)) return;
    const ageMs = Date.now() - new Date(user.created_at).getTime();
    if (ageMs > 14 * 24 * 60 * 60 * 1000) { localStorage.setItem(doneKey, '1'); return; }

    const { data, error } = await supabase.rpc('claim_referral', { code: storedRef() });
    if (error || typeof data !== 'string') return;
    if (data === 'not_signed_in') return; // try again next time
    // 'ok' = counted just now; 'already' on a brand-new account = the sign-up trigger counted them.
    if (data === 'ok' || (data === 'already' && ageMs < 24 * 60 * 60 * 1000)) track('referral_joined');
    localStorage.setItem(doneKey, '1');
    localStorage.removeItem(REF_KEY);
  } catch { /* ignore */ }
}

export async function getReferralStatus(): Promise<ReferralStatus | null> {
  try {
    const { data, error } = await supabase.rpc('my_referral_status');
    if (error || !data) return null;
    return data as ReferralStatus;
  } catch {
    return null;
  }
}

// Right after a session is created: shows "opened" on it if the sender has an
// active window or an earned unlock (which starts their 3 days). Never throws
// and never holds up the sender for more than a few seconds.
export async function unlockOpenedForSession(testId: string) {
  try {
    const call = supabase.rpc('unlock_opened_for_session', { p_test_id: testId });
    await Promise.race([call, new Promise(res => setTimeout(res, 4000))]);
  } catch { /* ignore */ }
}
